import * as Yup from 'yup';

import type { DocumentTemplateFormValues, ServiceFormValues } from 'types/servicesDocuments';
import { validateField, ValidationType } from './formValidation';

export const serviceFormInitialValues: ServiceFormValues = {
    service_code: '',
    country_code: '',
    name_en: '',
    name_fr: '',
    name_es: '',
    is_active: true,
    show_in_report: true,
    scope: 'provider',
    service_provider_id: '',
};

export const documentTemplateFormInitialValues: DocumentTemplateFormValues = {
    name_en: '',
    name_fr: '',
    description_en: '',
    description_fr: '',
    is_form: false,
    fields_json: '',
    document_type: '',
    category: '',
    country_code: '',
    template: null,
    sample: null,
    scope: 'provider',
    service_provider_id: '',
};

export const serviceFormValidationSchema = Yup.object().shape({
    name_en: validateField(ValidationType.STRING),
    name_fr: validateField(ValidationType.STRING, false),
    country_code: Yup.string().required('servicesDocuments.validation.countryRequired'),
    service_code: validateField(ValidationType.STRING, false),
    name_es: validateField(ValidationType.STRING, false),
    scope: Yup.string().oneOf(['universal', 'provider']).required(),
    service_provider_id: Yup.mixed().when('scope', {
        is: 'provider',
        then: () => Yup.number().required('servicesDocuments.validation.providerRequired'),
        otherwise: () => Yup.mixed().nullable(),
    }),
});

const isValidJson = (value: string | undefined): boolean => {
    if (!value?.trim()) {
        return true;
    }
    try {
        JSON.parse(value);
        return true;
    } catch {
        return false;
    }
};

export const documentTemplateFormValidationSchema = Yup.object().shape(
    {
        name_en: validateField(ValidationType.STRING, false).when('name_fr', {
            is: (nameFr: string) => !nameFr?.trim(),
            then: (schema) => schema.required('servicesDocuments.validation.nameRequired'),
        }),
        name_fr: validateField(ValidationType.STRING, false).when('name_en', {
            is: (nameEn: string) => !nameEn?.trim(),
            then: (schema) => schema.required('servicesDocuments.validation.nameRequired'),
        }),
        description_en: validateField(ValidationType.STRING, false),
        description_fr: validateField(ValidationType.STRING, false),
        fields_json: validateField(ValidationType.STRING, false).test(
            'valid-json',
            'servicesDocuments.validation.invalidJson',
            isValidJson
        ),
        document_type: validateField(ValidationType.STRING),
        country_code: Yup.string().when('scope', {
            is: 'universal',
            then: (schema) => schema.required('servicesDocuments.validation.countryRequired'),
            otherwise: (schema) => schema,
        }),
        category: validateField(ValidationType.STRING, false),
        template: Yup.mixed<File>().nullable(),
        sample: Yup.mixed<File>().nullable(),
        scope: Yup.string().oneOf(['universal', 'provider']).required(),
        service_provider_id: Yup.mixed().when('scope', {
            is: 'provider',
            then: () => Yup.number().required('servicesDocuments.validation.providerRequired'),
            otherwise: () => Yup.mixed().nullable(),
        }),
    },
    [['name_en', 'name_fr']]
);
